import type { SeriesDefaultAxes, SeriesFactory, SeriesModule, SeriesTooltipDefaults } from '../../module/coreModules';
import type { RequiredSeriesType } from '../../module/coreModulesTypes';
import type { ModuleContext } from '../../module/moduleContext';
import { deepClone } from '../../util/json';
import { mergeDefaults } from '../../util/object';
import type { SeriesType } from '../mapping/types';
import type { ISeries } from '../series/seriesTypes';
import { chartTypes, publicChartTypes } from './chartTypes';

interface SeriesRegistryRecord {
    moduleFactory?: SeriesFactory;
    defaultAxes?: SeriesDefaultAxes<RequiredSeriesType>;
    tooltipDefaults?: SeriesTooltipDefaults;
    solo?: boolean;
    groupable?: boolean;
    stackable?: boolean;
    stackedByDefault?: boolean;
}

class SeriesRegistry {
    private readonly seriesMap = new Map<SeriesType, SeriesRegistryRecord>();
    private readonly themeTemplates = new Map<string, object>();

    register(
        seriesType: NonNullable<SeriesType>,
        {
            chartTypes: [chartType],
            moduleFactory,
            tooltipDefaults,
            defaultAxes,
            themeTemplate,
            solo,
            stackable,
            groupable,
            stackedByDefault,
            hidden,
        }: SeriesModule
    ) {
        this.setThemeTemplate(seriesType, themeTemplate);
        this.seriesMap.set(seriesType, {
            moduleFactory,
            tooltipDefaults,
            defaultAxes,
            solo,
            stackable,
            groupable,
            stackedByDefault,
        });
        chartTypes.set(seriesType, chartType);
        if (!hidden) {
            publicChartTypes.set(seriesType, chartType);
        }
    }

    create(seriesType: SeriesType, moduleContext: ModuleContext): ISeries<any, any, any> {
        const seriesFactory = this.seriesMap.get(seriesType)?.moduleFactory;
        if (seriesFactory) {
            return seriesFactory(moduleContext);
        }
        throw new Error(`AG Charts - unknown series type: ${seriesType}`);
    }

    cloneDefaultAxes(seriesType: SeriesType) {
        const defaultAxes = this.seriesMap.get(seriesType)?.defaultAxes;
        if (defaultAxes == null) return null;
        return { axes: deepClone(defaultAxes) };
    }

    setThemeTemplate(seriesType: NonNullable<SeriesType>, themeTemplate: object) {
        const currentTemplate = this.themeTemplates.get(seriesType);
        this.themeTemplates.set(seriesType, mergeDefaults(themeTemplate, currentTemplate));
    }

    getThemeTemplate(seriesType: string) {
        return this.themeTemplates.get(seriesType);
    }

    getTooltipDefauls(seriesType: SeriesType) {
        return this.seriesMap.get(seriesType)?.tooltipDefaults;
    }

    isSolo(seriesType: SeriesType) {
        return this.seriesMap.get(seriesType)?.solo ?? false;
    }

    isGroupable(seriesType: SeriesType) {
        return this.seriesMap.get(seriesType)?.groupable ?? false;
    }

    isStackable(seriesType: SeriesType) {
        return this.seriesMap.get(seriesType)?.stackable ?? false;
    }

    isStackedByDefault(seriesType: SeriesType) {
        return this.seriesMap.get(seriesType)?.stackedByDefault ?? false;
    }
}

export const seriesRegistry = new SeriesRegistry();
